import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('adanos_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('adanos_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart(prevCart => {
      // If product has customizations or already has quantity set, add as new item
      if (product.customizations || product.quantity) {
        return [...prevCart, product];
      }
      
      // Otherwise, check if same product exists and increment quantity
      const existingItem = prevCart.find(item => item.id === product.id && !item.customizations);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id && !item.customizations
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const basePrice = item.price;
      const toppingsPrice = item.customizations?.toppings?.reduce((sum, t) => sum + t.price, 0) || 0;
      const saucesPrice = item.customizations?.sauces?.reduce((sum, s) => sum + s.price, 0) || 0;
      const sidesPrice = item.customizations?.sides?.reduce((sum, s) => sum + s.price, 0) || 0;
      const drinksPrice = item.customizations?.drinks?.reduce((sum, d) => sum + d.price, 0) || 0;
      const itemTotal = (basePrice + toppingsPrice + saucesPrice) * item.quantity + sidesPrice + drinksPrice;
      return total + itemTotal;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
